export enum AnalyticsLevel {
  company = 'company',
  project = 'project',
  deliverable_set = 'deliverable_set',
}

export enum BucketType {
  week = 'week',
  month = 'month',
}

export enum TimeRange {
  this_month = 'this_month',
  last_month = 'last_month',
  last_quarter = 'last_quarter',
  this_quarter = 'this_quarter',
  last_6_months = 'last_6_months',
  year_to_date = 'year_to_date',
}

export type GetNoOfProjectsParams = {
  company_id?: string | null
}

export type GetLateDeliverablesParams = {
  level: AnalyticsLevel
  company_id?: string | null
  project_id?: string | null
  deliverable_set_id?: string | null
}

export type GetCommentDensityParams = {
  level: AnalyticsLevel
  company_id?: string | null
  project_id?: string | null
  deliverable_set_id?: string | null
}

export type GetCompletionRateParams = {
  company_id?: string | null
  project_id?: string | null
  deliverable_set_id?: string | null
}

export type GetResolutionRateParams = {
  level: AnalyticsLevel
  company_id?: string | null
  project_id?: string | null
  deliverable_set_id?: string | null
}

export type GetResolutionRateByLevelParams = {
  level: AnalyticsLevel
  project_id?: string | null
  deliverable_set_id?: string | null
}

export type GetOpenCommentsParams = {
  level: AnalyticsLevel
  company_id?: string | null
  project_id?: string | null
  deliverable_set_id?: string | null
}

export type GetResolutionRateTrendParams = {
  bucket_type?: BucketType
  time_range?: TimeRange
  company_id?: string | null
}

export type GetReviewTimeTrendParams = {
  bucket_type?: BucketType
  time_range?: TimeRange
  company_id?: string | null
}

export type GetAvgDaysPerFileTrendParams = {
  bucket_type?: BucketType
  time_range?: TimeRange
  company_id?: string | null
}

export type GetAvgCycleTimeParams = {
  project_id: string
}

export type GetReviewCycleTimeParams = {
  deliverable_set_id: string
}

export type GetTflCompletionRateParams = {
  deliverable_set_id: string
}

export type GetTimeToFirstCommentParams = {
  deliverable_set_id: string
}

/**
 * Project with active/completed deliverable counts.
 */
export interface ProjectDeliverableStats {
  /**
   * Count of active deliverable sets
   * @minimum 0
   */
  active_deliverables: number
  /**
   * Count of completed deliverable sets
   * @minimum 0
   */
  completed_deliverables: number
  /** Unique project identifier */
  project_id: string
  /**
   * Project display name
   * @minLength 1
   * @maxLength 255
   */
  project_name: string
}

/**
 * Company-level late deliverable with project context.
 */
export interface CompanyLateDeliverable {
  /**
   * Deliverable set name
   * @minLength 1
   * @maxLength 255
   */
  deliverable_set_name: string
  /**
   * Number of days overdue (must be positive)
   */
  overdue_days: number
  /** Project identifier */
  project_id: string
  /**
   * Project name
   * @minLength 1
   * @maxLength 255
   */
  project_name: string
}

/**
 * Project-level late deliverable with deliverable set details.
 */
export interface ProjectLateDeliverable {
  /** Deliverable set identifier */
  deliverable_set_id: string
  /**
   * Deliverable set name
   * @minLength 1
   * @maxLength 255
   */
  deliverable_set_name: string
  /** Original due date */
  ds_due_date: string
  /**
   * Number of days overdue (must be positive)
   */
  overdue_days: number
}

/**
 * Project with comment density metrics.
 */
export interface ProjectCommentDensity {
  /**
   * Comments per file ratio
   * @minimum 0
   */
  comment_density: number
  /**
   * Total comment count
   * @minimum 0
   */
  no_of_comments: number
  /**
   * Total file count
   * @minimum 0
   */
  no_of_files: number
  project_id: string
  /**
   * @minLength 1
   * @maxLength 255
   */
  project_name: string
}

/**
 * Deliverable set with comment density metrics.
 */
export interface DeliverableSetCommentDensity {
  /**
   * Comments per file ratio
   * @minimum 0
   */
  comment_density: number
  deliverable_set_id: string
  /**
   * @minLength 1
   * @maxLength 255
   */
  ds_name: string
  /**
   * Total comment count
   * @minimum 0
   */
  no_of_comments: number
  /**
   * Total file count
   * @minimum 0
   */
  no_of_files: number
}

/**
 * Response for no-of-projects endpoint.
 */
export interface ResponseNoOfProjects {
  /** List of all projects with deliverable counts */
  data?: ProjectDeliverableStats[]
  /**
   * Total count of projects
   * @minimum 0
   */
  metric: number
}

/**
 * List of late deliverables sorted by overdue_days DESC
 */
export type ResponseLateDeliverablesData =
  | CompanyLateDeliverable[]
  | ProjectLateDeliverable[]

/**
 * Response for late-deliverables endpoint.
 */
export interface ResponseLateDeliverables {
  /** List of late deliverables sorted by overdue_days DESC */
  data?: ResponseLateDeliverablesData
  /**
   * Total count of late deliverables
   * @minimum 0
   */
  metric: number
}

export type ResponseCommentDensityData =
  | ProjectCommentDensity[]
  | DeliverableSetCommentDensity[]

/**
 * Response for comment-density endpoint.
 */
export interface ResponseCommentDensity {
  data?: ResponseCommentDensityData
  /**
   * Average comment density across items
   * @minimum 0
   */
  metric: number
}

/**
 * Completion rate metrics.
 */
export interface CompletionRate {
  /**
   * Count of approved deliverable sets
   * @minimum 0
   */
  approved_count: number
  /**
   * Total count of deliverable sets
   */
  total_count: number
}

/**
 * Response for completion-rate endpoint.
 */
export interface ResponseCompletionRate {
  data?: CompletionRate[]
  /**
   * Completion rate percentage
   * @minimum 0
   * @maximum 100
   */
  metric: number
}

/**
 * Resolution rate metrics.
 */
export interface ResolutionRate {
  /**
   * Count of open comments
   * @minimum 0
   */
  open_count: number
  /**
   * Count of resolved comments
   * @minimum 0
   */
  resolved_count: number
}

/**
 * Response for resolution-rate endpoint.
 */
export interface ResponseResolutionRate {
  data?: ResolutionRate[]
  /**
   * Resolution rate percentage
   * @minimum 0
   * @maximum 100
   */
  metric: number
}

/**
 * Open comments metrics.
 */
export interface OpenComments {
  /**
   * Total open comments count
   * @minimum 0
   */
  open_comment_count: number
}

/**
 * Response for open-comments endpoint.
 */
export interface ResponseOpenComments {
  data?: OpenComments[]
  /**
   * Total open comments count
   * @minimum 0
   */
  metric: number
}

/**
 * Single data point in trend response.
 */
export interface ResolutionRateTrendDataPoint {
  /**
   * Average resolution rate for bucket
   * @minimum 0
   * @maximum 100
   */
  avg_resolution_rate_percentage: number
  /** Start date of time bucket */
  bucket_date: string
}

/**
 * Response for resolution rate trend endpoint.
 */
export interface ResponseResolutionRateTrend {
  /** Time-series data points sorted chronologically */
  data?: ResolutionRateTrendDataPoint[]
  /**
   * Overall average resolution rate across all buckets (%)
   * @minimum 0
   * @maximum 100
   */
  metric: number
}

/**
 * Single data point in review time trend response.
 */
export interface ReviewTimeTrendDataPoint {
  /**
   * Average review cycle time in days for bucket
   * @minimum 0
   */
  avg_cycle_time_days: number
  /** Start date of time bucket */
  bucket_date: string
}

/**
 * Response for review time trend endpoint.
 */
export interface ResponseReviewTimeTrend {
  /** Time-series data points sorted chronologically */
  data?: ReviewTimeTrendDataPoint[]
  /**
   * Overall average review cycle time across all buckets (days)
   * @minimum 0
   */
  metric: number
}

/**
 * Individual deliverable set cycle time data.
 */
export interface DeliverableSetCycleTime {
  /** Deliverable set identifier */
  deliverable_set_id: string
  /**
   * Deliverable set name (ds_name from API)
   * @minLength 1
   * @maxLength 255
   */
  ds_name: string
  /**
   * Cycle time from creation to approval in days (ceiling rounded)
   * @minimum 0
   */
  cycle_time_days: number
}

/**
 * Response for average cycle time endpoint.
 */
export interface ResponseAvgCycleTime {
  /** List of all approved deliverable sets with individual cycle times */
  data?: DeliverableSetCycleTime[]
  /**
   * Overall average cycle time across all approved deliverable sets (days)
   * @minimum 0
   */
  metric: number
}

/**
 * Individual cycle time data for a deliverable set.
 */
export interface ReviewCycleTimeData {
  /**
   * Cycle time from creation to approval in days (ceiling rounded)
   * @minimum 0
   */
  cycle_time_days: number
}

/**
 * Response for review cycle time endpoint at deliverable set level.
 */
export interface ResponseReviewCycleTime {
  /** List with cycle time breakdown (returns 0 if not approved) */
  data?: ReviewCycleTimeData[]
  /**
   * Review cycle time in days (0 if not approved)
   * @minimum 0
   */
  metric: number
}

/**
 * TFL completion rate data for a deliverable set.
 */
export interface TflCompletionRateData {
  /**
   * Number of approved files
   * @minimum 0
   */
  approved_files: number
  /**
   * Total number of files
   * @minimum 0
   */
  total_files: number
}

/**
 * Response for TFL completion rate endpoint at deliverable set level.
 */
export interface ResponseTflCompletionRate {
  /** List with completion rate breakdown (approved_files, total_files) */
  data?: TflCompletionRateData[]
  /**
   * Overall completion rate percentage (approved_files / total_files * 100)
   * @minimum 0
   */
  metric: number
}

/**
 * Parameters for getting DS completion rate at project level.
 */
export type GetDsCompletionRateParams = {
  /** Required - Project ID to get completion rate for */
  project_id: string
}

/**
 * Parameters for getting approved files metrics with bucket drill-down.
 */
export type GetApprovedFilesParams = {
  /** Required - Aggregation period ("week" or "month"). Default: week */
  bucket_type?: BucketType
  /** Required - Predefined time range. Default: this_month */
  time_range?: TimeRange
  /** Optional - Filter by specific company */
  company_id?: string | null
}

/**
 * DS completion rate data at project level.
 */
export interface DsCompletionRateData {
  /**
   * Number of approved deliverable sets
   * @minimum 0
   */
  approved_count: number
  /**
   * Total number of deliverable sets
   * @minimum 0
   */
  total_count: number
}

/**
 * Response for DS completion rate endpoint at project level.
 */
export interface ResponseDsCompletionRate {
  /** List with completion rate breakdown (approved_count, total_count) */
  data?: DsCompletionRateData[]
  /**
   * Overall completion rate percentage (approved_count / total_count * 100)
   * @minimum 0
   * @maximum 100
   */
  metric: number
}

/**
 * Single data point in approved files metric response with bucket breakdown.
 */
export interface ApprovedFilesDataPoint {
  /**
   * Count of approved files in this bucket
   * @minimum 0
   */
  approved_files_count: number
  /** Start date of time bucket */
  bucket_date: string
}

/**
 * Response for approved files metrics endpoint with bucket drill-down.
 */
export interface ResponseApprovedFiles {
  /** Bucket-level breakdown sorted chronologically by bucket_date (earliest first) */
  data?: ApprovedFilesDataPoint[]
  /**
   * Total count of approved files across entire time period
   * @minimum 0
   */
  metric: number
}

/**
 * Time to first comment data for a deliverable set.
 */
export interface TimeToFirstCommentData {
  /**
   * Time from first document upload to first comment in days
   * @minimum 0
   */
  time_to_first_comment_days: number
}

/**
 * Response for time-to-first-comment endpoint at deliverable set level.
 */
export interface ResponseTimeToFirstComment {
  /** List with time to first comment breakdown */
  data?: TimeToFirstCommentData[]
  /**
   * Time to first comment in days (0 if no comments exist)
   * @minimum 0
   */
  metric: number
}

/**
 * Single data point in avg days per file trend response.
 */
export interface AvgDaysPerFileDataPoint {
  /**
   * Average days per file for bucket
   * @minimum 0
   */
  avg_days: number
  /** Start date of time bucket */
  bucket_date: string
}

/**
 * Response for avg days per file trend endpoint.
 */
export interface ResponseAvgDaysPerFileTrend {
  /** Time-series data points sorted chronologically by bucket_date (earliest first) */
  data?: AvgDaysPerFileDataPoint[]
  /**
   * Overall weighted average days per file across entire time period
   * @minimum 0
   */
  metric: number
}

/**
 * Most active user data from audit activity
 */
export interface MostActiveUser {
  /** User ID */
  user_id: string
  /** Full name of the user */
  full_name: string
  /** Number of file views */
  no_of_views: number
  /** Number of comments created */
  no_of_comments: number
  /** Number of replies created */
  no_of_replies: number
  /** Number of status changes */
  no_of_status_changes: number
}

/**
 * Response for most active users endpoint.
 */
export interface ResponseMostActiveUsers {
  /** List of top 5 most active users sorted by total activity */
  data?: MostActiveUser[]
  /**
   * Total count of users returned (always 5 or less)
   * @minimum 0
   */
  metric: number
}
